import { useEffect, useState } from "react";
import { Badge, Button, Card, MUTED_TEXT, Spinner, Switch, stackStyle } from "@dayvise/react-ui";
import { useYoloMode, useYoloModeMutations } from "./hooks.ts";

/**
 * Elevated autonomy, stated honestly.
 *
 * The switch records a decision for one scope and is audited; it is not a grant.
 * Permissions, path allowlists, approvals and quotas decide what a run may do, and
 * this panel says so next to the control rather than implying otherwise.
 */
export function YoloModePanel() {
  const query = useYoloMode();
  const mutations = useYoloModeMutations();
  const [workspaceAcknowledged, setWorkspaceAcknowledged] = useState(false);
  const mode = query.data?.mode;
  const enabled = mode?.effective === true;

  useEffect(() => {
    if (mode?.canManageWorkspace !== true) setWorkspaceAcknowledged(false);
  }, [mode?.canManageWorkspace]);

  const error = query.error ?? mutations.update.error;
  return (
    <Card
      title="Elevated autonomy (YOLO mode)"
      subtitle="A recorded preference for this scope. It authorizes nothing by itself: tool permissions, path allowlists, approvals and quotas still decide what a run may do."
    >
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        {query.isPending || mode === undefined ? <Spinner label="Loading autonomy settings" /> : null}
        {error ? <p role="alert" style={{ color: "var(--dayvise-color-danger)" }}>{error instanceof Error ? error.message : "Could not read or save the autonomy setting"}</p> : null}
        {mode !== undefined ? (
          <>
            <div style={stackStyle("var(--dayvise-space-xs)")}>
              <Badge tone={enabled ? "warning" : "neutral"}>{enabled ? "Mode on" : "Mode off"}</Badge>
              {mode.userEnabled ? <Badge tone="neutral">Your own scope</Badge> : null}
              {mode.workspaceEnabled ? <Badge tone="neutral">Workspace scope</Badge> : null}
            </div>
            <Switch
              label={mode.userEnabled ? "On for you" : "Off for you"}
              description="Applies to your own runs in this workspace. A workspace-wide setting, when one exists, is what makes it effective for everyone."
              checked={mode.userEnabled}
              disabled={mutations.update.isPending}
              onChange={(checked) => mutations.update.mutate({ scopeKind: "user", enabled: checked })}
            />
            {mode.canManageWorkspace ? (
              <Switch
                label={mode.workspaceEnabled ? "On for the whole workspace" : "Off for the whole workspace"}
                description="Only an owner or administrator can change this. Members cannot override it for themselves, and it grants no access to anybody's files or tools."
                checked={mode.workspaceEnabled}
                disabled={mutations.update.isPending || (!workspaceAcknowledged && !mode.workspaceEnabled)}
                onChange={(checked) => mutations.update.mutate({ scopeKind: "workspace", enabled: checked })}
              />
            ) : null}
            {mode.canManageWorkspace && !mode.workspaceEnabled ? (
              <Switch
                label="I understand this changes the workspace default"
                description="Confirm before the workspace-wide switch becomes available."
                checked={workspaceAcknowledged}
                onChange={setWorkspaceAcknowledged}
              />
            ) : null}
            <p style={{ ...MUTED_TEXT, margin: 0 }}>
              Turning this on does not widen what a run may read, write, execute or spend today, and it never overrides an approval: a denied path or action stays denied while the mode is on, and an action that requires approval still asks. Reach beyond the workspace, device files and deletion rules are separate capabilities, not this switch.
            </p>
            {mutations.update.isPending ? <Spinner label="Saving" /> : null}
            <Button size="sm" variant="ghost" loading={query.isFetching} onClick={() => void query.refetch()}>Reload</Button>
          </>
        ) : null}
      </div>
    </Card>
  );
}
